import { useStyles } from './styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import WdPaper from './WdPaper/WdPaper';
import React, { useEffect } from 'react';
import validationSchema from './validationSchema';
import { useSelector, useDispatch } from 'react-redux';
import SupplyTypeTemperature from './Input/SupplyTypeTemperature';
import InputVoltage from './Input/InputVoltage';
import Output from './Output';
import { evalWithWebDesigner, resetStatus } from './../../redux/slices/categoryDataSlice';
import { resetRowsPerPage, resetSortOrder } from './../../components/ProductsTable/productsTableSlice';
import { parseInputParameters } from './utils';
import { setInputParameters } from '../../redux/slices/wdValidatorSlice';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { INPUT_PARAM_DEFAULTS } from './constants';
import { useFormik } from 'formik';
import {clickEvalWithWebDesignerTagGA4, pushToGTM} from '../../utils/analytics/gtm';
import { useTranslation } from 'react-i18next';

const WdValidator = ({ handleReset, isResetClicked, setIsResetClicked }) => {
	const { t } = useTranslation();
	const inputParametersDL = useSelector(state => state.wdValidator.inputParameters);
	const dispatch = useDispatch();
	const applicationOriented = useSelector(state => state.categoryData.applicationOriented);
	const classes = useStyles({ applicationOriented });
	const appliedStateOfQuickFilter = useSelector(state => state.filters.appliedState);
	const supplyTypeState = appliedStateOfQuickFilter.pwcv.value.toString().substring(0, 2);
	const vinMinState = appliedStateOfQuickFilter.vin.value;
	const vinMaxState = appliedStateOfQuickFilter.vim.value;
	const min = useSelector(state => state.filters.filters[2].constraints.minValue);
	const max = useSelector(state => state.filters.filters[2].constraints.maxValue);
	const isVoutInvalid =
		min && max ? appliedStateOfQuickFilter.voapo.value < min || appliedStateOfQuickFilter.voapo.value > max : false;

	function checkForChangesAfterEval(values, inputParametersDL) {
		parseFloat(values.vinMin) === inputParametersDL.vinMin &&
			parseFloat(values.vinMax) === inputParametersDL.vinMax &&
			parseFloat(values.vout) === inputParametersDL.vout &&
			parseFloat(values.iout) === inputParametersDL.iout &&
			parseFloat(values.ambientTemperature) === inputParametersDL.ambientTemperature &&
			values.supplyType === inputParametersDL.supplyType &&
			parseFloat(values.lineFrequency) === inputParametersDL.lineFrequency;
	}

	useEffect(() => {
		if (isResetClicked) {
			handleReset();
			setIsResetClicked(false);
		}
	}, [isResetClicked, handleReset]);

	const history = useHistory();
	let location = useLocation();

	function handleFormSubmit(values) {
		const inputParameters = parseInputParameters(values);
		dispatch(setInputParameters(inputParameters));
		dispatch(evalWithWebDesigner(values));

		if (!location.pathname.endsWith('/wdValidated')) {
			const inputParametersQuery = queryString.stringify(inputParameters);
			history.push(location.pathname + '/wdValidated?' + inputParametersQuery);
		} else if (location.pathname.endsWith('/wdValidated') && !!location.search) {
			const inputParametersQuery = queryString.stringify(inputParameters);
			history.push(location.pathname + '?' + inputParametersQuery);
		}
		pushToGTM(clickEvalWithWebDesignerTagGA4());

		return () => {
			dispatch(resetStatus());
			dispatch(resetRowsPerPage());
			dispatch(resetSortOrder());
		};
	}

	const formik = useFormik({
		initialValues: {
			vinMin: inputParametersDL.vinMin,
			vinMax: inputParametersDL.vinMax,
			vout: inputParametersDL.vout,
			iout: inputParametersDL.iout,
			ambientTemperature: inputParametersDL.ambientTemperature,
			supplyType: inputParametersDL.supplyType,
			lineFrequency: inputParametersDL.lineFrequency,
			isolated: false
		},
		validationSchema: validationSchema,
		onSubmit: (values, { setSubmitting }) => {
			setSubmitting(false);
			handleFormSubmit(values);
		}
	});

	function handleReset() {
		formik.setValues({
			ambientTemperature: INPUT_PARAM_DEFAULTS.AMB_TEMP,
			lineFrequency: INPUT_PARAM_DEFAULTS.LINE_FREQUENCY,
			vout: appliedStateOfQuickFilter.voapo.value
		});
	}

	const { errors, values, setValues, isSubmitting, handleChange, handleSubmit } = formik;
	const isValid = !Object.keys(errors).length;

	return (
		<form onSubmit={handleSubmit}>
			<WdPaper applicationOriented={applicationOriented}>
				<Grid container spacing={1}>
					<Grid style={{ fontSize: '16px', color: 'red', marginLeft: '7px', marginBottom: '4px' }}>
						{supplyTypeState === 'Al' ||
						!appliedStateOfQuickFilter.vin.isDirty ||
						!appliedStateOfQuickFilter.vim.isDirty ||
						!appliedStateOfQuickFilter.voapo.used ||
						isVoutInvalid ||
						!appliedStateOfQuickFilter.io.isDirty
							? t('main:components.wdValidators.wdValidator.noParameters') + ':\u00a0  \u00a0'
							: ''}
						<span style={{ fontWeight: 'bold' }}>
							{supplyTypeState === 'Al' && t('main:filters.pwcv') + ' \u00a0 \u00a0'}
							{!appliedStateOfQuickFilter.vim.isDirty && supplyTypeState === 'Al' && (
								<span>
									V
									<sub>
										IN<sub>DC/RMS</sub> (max)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.vim.isDirty && supplyTypeState === 'DC' && (
								<span>
									V
									<sub>
										IN<sub>DC</sub> (max)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.vim.isDirty && supplyTypeState === 'AC' && (
								<span>
									V
									<sub>
										IN<sub>RMS</sub> (max)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.vin.isDirty && supplyTypeState === 'Al' && (
								<span>
									V
									<sub>
										IN<sub>DC/RMS</sub> (min)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.vin.isDirty && supplyTypeState === 'DC' && (
								<span>
									V
									<sub>
										IN<sub>DC</sub> (min)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.vin.isDirty && supplyTypeState === 'AC' && (
								<span>
									V
									<sub>
										IN<sub>RMS</sub> (min)
									</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{(!appliedStateOfQuickFilter.voapo.used || isVoutInvalid) && (
								<span>
									V<sub>OUT</sub>
									{'\u00a0 \u00a0 \u00a0'}
								</span>
							)}
							{!appliedStateOfQuickFilter.io.isDirty && (
								<span>
									I<sub>OUT</sub>
								</span>
							)}
						</span>
					</Grid>
				</Grid>
				<Grid container justifyContent="space-between">
					<Grid className={clsx(classes.firstColumn)}>
						<InputVoltage formikProps={formik} />
					</Grid>
					<Grid className={clsx(classes.secondColumn)}>
						<Output formikProps={formik} />
					</Grid>

					<Grid className={clsx(classes.thirdColumn)}>
						<SupplyTypeTemperature formikProps={formik} />
					</Grid>
					<Grid className={clsx(classes.fourthColumn)}>
						<Grid>
							<Grid style={{ height: '77px' }}></Grid>
						</Grid>
						<Grid style={{ display: 'flex', justifyContent: 'flex-end' }}>
							<Button
								id="WdValidator__evalWithWebDesigner+_button"
								className={clsx(classes.evalButton)}
								color="primary"
								variant="contained"
								onChange={handleChange}
								values={values}
								setValues={setValues}
								disabled={
									!isValid ||
									(applicationOriented && checkForChangesAfterEval(values, inputParametersDL)) ||
									supplyTypeState === 'Al' ||
									appliedStateOfQuickFilter.vin.isDirty === false ||
									appliedStateOfQuickFilter.vim.isDirty === false ||
									appliedStateOfQuickFilter.io.isDirty === false ||
									appliedStateOfQuickFilter.voapo.used === false ||
									vinMinState > vinMaxState ||
									isSubmitting ||
									isVoutInvalid
								}
								type="submit">
								{t('main:components.wdValidators.wdValidator.button.eval')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</WdPaper>
		</form>
	);
};

WdValidator.propTypes = {
	values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool, PropTypes.object]))
};

export default WdValidator;
